import { isMetal } from './metals';
import { CoinListItem } from '../types';

export type AssetType = 'crypto' | 'metal' | 'stock' | 'etf';

export function isStock(id: string): boolean {
  return id.startsWith('stock:');
}

export function isETF(id: string): boolean {
  return id.startsWith('etf:');
}

export function isStockOrETF(id: string): boolean {
  return id.startsWith('stock:') || id.startsWith('etf:');
}

/** Strip the asset-type prefix to get the raw Yahoo Finance ticker */
export function tickerFromId(id: string): string {
  return id.includes(':') ? id.split(':')[1] : id;
}

/** Build a stable asset ID from a Yahoo Finance quote */
export function makeStockId(ticker: string, quoteType: string): string {
  const type = quoteType === 'ETF' || quoteType === 'MUTUALFUND' ? 'etf' : 'stock';
  return `${type}:${ticker.toUpperCase()}`;
}

export function getAssetType(id: string): AssetType {
  if (isMetal(id)) return 'metal';
  if (id.startsWith('etf:')) return 'etf';
  if (id.startsWith('stock:')) return 'stock';
  return 'crypto';
}

export const ASSET_BADGE: Record<AssetType, { label: string; className: string }> = {
  crypto: { label: 'Crypto', className: 'bg-white/[0.06] text-slate-400'    },
  metal:  { label: 'Metal',  className: 'bg-yellow-500/15 text-yellow-500'  },
  stock:  { label: 'Stock',  className: 'bg-blue-500/15 text-blue-400'      },
  etf:    { label: 'ETF',    className: 'bg-white/[0.06] text-slate-400'    },
};

// ── Curated stock/ETF universe ────────────────────────────────────────────────
// The app is crypto-first; stocks/ETFs are a supporting feature. Rather than an
// open Yahoo search (which surfaces obscure tickers and CoinGecko tokenized-stock
// lookalikes), we support a hand-picked list of major, liquid assets only.
//
// `id` is the stable, clean asset id (via makeStockId) used for storage/display.
// `ticker` is the Yahoo Finance fetch symbol — for ASX listings it carries the
// ".AX" suffix (e.g. VAS.AX) while the display symbol stays clean ("VAS"). Use
// yahooTicker(id) at price-fetch time so the suffix never leaks into the UI.

export interface SupportedStock {
  /** Clean asset id, e.g. 'stock:NVDA' / 'etf:QQQ'. */
  id: string;
  /** Yahoo Finance fetch ticker, e.g. 'NVDA' / 'VAS.AX'. */
  ticker: string;
  /** Display name shown under the symbol. */
  name: string;
  type: 'stock' | 'etf';
}

// Names/types are sourced from Yahoo (via a one-off validation pass, see
// scratchpad/validate-tickers.js) — every ticker below returned a live quote at
// build time. Weighted toward an AU investor: ASX top ~100 + ASX ETFs, plus US
// large caps and popular US ETFs. To refresh, re-run the validation script.
export const SUPPORTED_STOCKS: SupportedStock[] = [
  // ── US mega-cap stocks ──────────────────────────────────────────────────────
  { id: 'stock:AAPL', ticker: 'AAPL', name: "Apple", type: 'stock' },
  { id: 'stock:MSFT', ticker: 'MSFT', name: "Microsoft", type: 'stock' },
  { id: 'stock:NVDA', ticker: 'NVDA', name: "NVIDIA", type: 'stock' },
  { id: 'stock:AMZN', ticker: 'AMZN', name: "Amazon", type: 'stock' },
  { id: 'stock:GOOGL', ticker: 'GOOGL', name: "Alphabet", type: 'stock' },
  { id: 'stock:GOOG', ticker: 'GOOG', name: "Alphabet Inc.", type: 'stock' },
  { id: 'stock:META', ticker: 'META', name: "Meta", type: 'stock' },
  { id: 'stock:TSLA', ticker: 'TSLA', name: "Tesla", type: 'stock' },
  { id: 'stock:BRK-B', ticker: 'BRK-B', name: "Berkshire Hathaway Inc.", type: 'stock' },
  { id: 'stock:AVGO', ticker: 'AVGO', name: "Broadcom Inc.", type: 'stock' },
  { id: 'stock:JPM', ticker: 'JPM', name: "JP Morgan Chase & Co.", type: 'stock' },
  { id: 'stock:V', ticker: 'V', name: "Visa Inc.", type: 'stock' },
  { id: 'stock:MA', ticker: 'MA', name: "Mastercard Incorporated", type: 'stock' },
  { id: 'stock:LLY', ticker: 'LLY', name: "Eli Lilly and Company", type: 'stock' },
  { id: 'stock:UNH', ticker: 'UNH', name: "UnitedHealth Group Incorporated", type: 'stock' },
  { id: 'stock:XOM', ticker: 'XOM', name: "ExxonMobil Holdings Corporation", type: 'stock' },
  { id: 'stock:JNJ', ticker: 'JNJ', name: "Johnson & Johnson", type: 'stock' },
  { id: 'stock:PG', ticker: 'PG', name: "Procter & Gamble Company (The)", type: 'stock' },
  { id: 'stock:HD', ticker: 'HD', name: "Home Depot, Inc. (The)", type: 'stock' },
  { id: 'stock:CVX', ticker: 'CVX', name: "Chevron Corporation", type: 'stock' },
  { id: 'stock:MRK', ticker: 'MRK', name: "Merck & Company, Inc.", type: 'stock' },
  { id: 'stock:ABBV', ticker: 'ABBV', name: "AbbVie Inc.", type: 'stock' },
  { id: 'stock:PEP', ticker: 'PEP', name: "PepsiCo, Inc.", type: 'stock' },
  { id: 'stock:KO', ticker: 'KO', name: "Coca-Cola Company (The)", type: 'stock' },
  { id: 'stock:COST', ticker: 'COST', name: "Costco Wholesale Corporation", type: 'stock' },
  { id: 'stock:WMT', ticker: 'WMT', name: "Walmart Inc.", type: 'stock' },
  { id: 'stock:BAC', ticker: 'BAC', name: "Bank of America Corporation", type: 'stock' },
  { id: 'stock:ADBE', ticker: 'ADBE', name: "Adobe Inc.", type: 'stock' },
  { id: 'stock:CRM', ticker: 'CRM', name: "Salesforce, Inc.", type: 'stock' },
  { id: 'stock:NFLX', ticker: 'NFLX', name: "Netflix, Inc.", type: 'stock' },
  { id: 'stock:AMD', ticker: 'AMD', name: "Advanced Micro Devices, Inc.", type: 'stock' },
  { id: 'stock:ACN', ticker: 'ACN', name: "Accenture plc", type: 'stock' },
  { id: 'stock:LIN', ticker: 'LIN', name: "Linde plc", type: 'stock' },
  { id: 'stock:MCD', ticker: 'MCD', name: "McDonald's Corporation", type: 'stock' },
  { id: 'stock:TMO', ticker: 'TMO', name: "Thermo Fisher Scientific Inc", type: 'stock' },
  { id: 'stock:ABT', ticker: 'ABT', name: "Abbott Laboratories", type: 'stock' },
  { id: 'stock:DHR', ticker: 'DHR', name: "Danaher Corporation", type: 'stock' },
  { id: 'stock:CSCO', ticker: 'CSCO', name: "Cisco Systems, Inc.", type: 'stock' },
  { id: 'stock:INTC', ticker: 'INTC', name: "Intel Corporation", type: 'stock' },
  { id: 'stock:QCOM', ticker: 'QCOM', name: "QUALCOMM Incorporated", type: 'stock' },
  { id: 'stock:TXN', ticker: 'TXN', name: "Texas Instruments Incorporated", type: 'stock' },
  { id: 'stock:PM', ticker: 'PM', name: "Philip Morris International Inc", type: 'stock' },
  { id: 'stock:NKE', ticker: 'NKE', name: "NIKE, Inc.", type: 'stock' },
  { id: 'stock:WFC', ticker: 'WFC', name: "Wells Fargo & Company", type: 'stock' },
  { id: 'stock:UPS', ticker: 'UPS', name: "United Parcel Service, Inc.", type: 'stock' },
  { id: 'stock:MS', ticker: 'MS', name: "Morgan Stanley", type: 'stock' },
  { id: 'stock:DIS', ticker: 'DIS', name: "Walt Disney Company (The)", type: 'stock' },
  { id: 'stock:VZ', ticker: 'VZ', name: "Verizon Communications Inc.", type: 'stock' },
  { id: 'stock:CMCSA', ticker: 'CMCSA', name: "Comcast Corporation", type: 'stock' },
  { id: 'stock:INTU', ticker: 'INTU', name: "Intuit Inc.", type: 'stock' },
  { id: 'stock:AMGN', ticker: 'AMGN', name: "Amgen Inc.", type: 'stock' },
  { id: 'stock:NEE', ticker: 'NEE', name: "NextEra Energy, Inc.", type: 'stock' },
  { id: 'stock:PFE', ticker: 'PFE', name: "Pfizer, Inc.", type: 'stock' },
  { id: 'stock:LOW', ticker: 'LOW', name: "Lowe's Companies, Inc.", type: 'stock' },
  { id: 'stock:UNP', ticker: 'UNP', name: "Union Pacific Corporation", type: 'stock' },
  { id: 'stock:RTX', ticker: 'RTX', name: "RTX Corporation", type: 'stock' },
  { id: 'stock:HON', ticker: 'HON', name: "Honeywell International Inc.", type: 'stock' },
  { id: 'stock:SPGI', ticker: 'SPGI', name: "S&P Global Inc.", type: 'stock' },
  { id: 'stock:GE', ticker: 'GE', name: "GE Aerospace", type: 'stock' },
  { id: 'stock:CAT', ticker: 'CAT', name: "Caterpillar, Inc.", type: 'stock' },
  { id: 'stock:BA', ticker: 'BA', name: "The Boeing Company", type: 'stock' },
  { id: 'stock:GS', ticker: 'GS', name: "Goldman Sachs Group, Inc. (The)", type: 'stock' },
  { id: 'stock:BLK', ticker: 'BLK', name: "BlackRock, Inc.", type: 'stock' },
  { id: 'stock:ELV', ticker: 'ELV', name: "Elevance Health, Inc.", type: 'stock' },
  { id: 'stock:BKNG', ticker: 'BKNG', name: "Booking Holdings Inc.", type: 'stock' },
  { id: 'stock:DE', ticker: 'DE', name: "Deere & Company", type: 'stock' },
  { id: 'stock:MDT', ticker: 'MDT', name: "Medtronic plc.", type: 'stock' },
  { id: 'stock:ADP', ticker: 'ADP', name: "Automatic Data Processing, Inc.", type: 'stock' },
  { id: 'stock:GILD', ticker: 'GILD', name: "Gilead Sciences, Inc.", type: 'stock' },
  { id: 'stock:ISRG', ticker: 'ISRG', name: "Intuitive Surgical, Inc.", type: 'stock' },
  { id: 'stock:VRTX', ticker: 'VRTX', name: "Vertex Pharmaceuticals Incorporated", type: 'stock' },
  { id: 'stock:REGN', ticker: 'REGN', name: "Regeneron Pharmaceuticals, Inc.", type: 'stock' },
  { id: 'stock:CB', ticker: 'CB', name: "Chubb Limited", type: 'stock' },
  { id: 'stock:ZTS', ticker: 'ZTS', name: "Zoetis Inc.", type: 'stock' },
  { id: 'stock:NOW', ticker: 'NOW', name: "ServiceNow, Inc.", type: 'stock' },
  { id: 'stock:SYK', ticker: 'SYK', name: "Stryker Corporation", type: 'stock' },
  { id: 'stock:PGR', ticker: 'PGR', name: "The Progressive Corporation", type: 'stock' },
  { id: 'stock:C', ticker: 'C', name: "Citigroup Inc.", type: 'stock' },
  { id: 'stock:SCHW', ticker: 'SCHW', name: "The Charles Schwab Corporation", type: 'stock' },
  { id: 'stock:T', ticker: 'T', name: "AT&T Inc.", type: 'stock' },
  { id: 'stock:MO', ticker: 'MO', name: "Altria Group, Inc.", type: 'stock' },
  { id: 'stock:CI', ticker: 'CI', name: "The Cigna Group", type: 'stock' },
  { id: 'stock:SO', ticker: 'SO', name: "Southern Company (The)", type: 'stock' },
  { id: 'stock:DUK', ticker: 'DUK', name: "Duke Energy Corporation", type: 'stock' },
  { id: 'stock:BDX', ticker: 'BDX', name: "Becton, Dickinson and Company", type: 'stock' },
  { id: 'stock:PLD', ticker: 'PLD', name: "Prologis, Inc.", type: 'stock' },
  { id: 'stock:AMT', ticker: 'AMT', name: "American Tower Corporation", type: 'stock' },
  { id: 'stock:PANW', ticker: 'PANW', name: "Palo Alto Networks, Inc.", type: 'stock' },
  { id: 'stock:MU', ticker: 'MU', name: "Micron Technology, Inc.", type: 'stock' },
  { id: 'stock:MSTR', ticker: 'MSTR', name: "MicroStrategy", type: 'stock' },
  { id: 'stock:COIN', ticker: 'COIN', name: "Coinbase", type: 'stock' },

  // ── US ETFs ─────────────────────────────────────────────────────────────────
  { id: 'etf:SPY', ticker: 'SPY', name: "S&P 500", type: 'etf' },
  { id: 'etf:QQQ', ticker: 'QQQ', name: "Nasdaq 100", type: 'etf' },
  { id: 'etf:VOO', ticker: 'VOO', name: "Vanguard S&P 500 ETF", type: 'etf' },
  { id: 'etf:VTI', ticker: 'VTI', name: "Total US Market", type: 'etf' },
  { id: 'etf:VUG', ticker: 'VUG', name: "Vanguard Morningstar Growth ETF", type: 'etf' },
  { id: 'etf:VTV', ticker: 'VTV', name: "Vanguard Morningstar Value ETF", type: 'etf' },
  { id: 'etf:SCHD', ticker: 'SCHD', name: "Schwab U.S. Dividend Equity ETF", type: 'etf' },
  { id: 'etf:GLD', ticker: 'GLD', name: "SPDR Gold Shares", type: 'etf' },
  { id: 'etf:SLV', ticker: 'SLV', name: "iShares Silver Trust", type: 'etf' },
  { id: 'etf:DIA', ticker: 'DIA', name: "Dow Jones", type: 'etf' },
  { id: 'etf:IWM', ticker: 'IWM', name: "Russell 2000", type: 'etf' },

  // ── ASX top ~100 stocks ─────────────────────────────────────────────────────
  { id: 'stock:BHP', ticker: 'BHP.AX', name: "BHP Group Limited", type: 'stock' },
  { id: 'stock:CBA', ticker: 'CBA.AX', name: "Commonwealth Bank of Australia", type: 'stock' },
  { id: 'stock:CSL', ticker: 'CSL.AX', name: "CSL", type: 'stock' },
  { id: 'stock:NAB', ticker: 'NAB.AX', name: "National Australia Bank", type: 'stock' },
  { id: 'stock:WBC', ticker: 'WBC.AX', name: "Westpac Banking Corporation", type: 'stock' },
  { id: 'stock:ANZ', ticker: 'ANZ.AX', name: "ANZ Group", type: 'stock' },
  { id: 'stock:MQG', ticker: 'MQG.AX', name: "Macquarie Group", type: 'stock' },
  { id: 'stock:WES', ticker: 'WES.AX', name: "Wesfarmers Limited", type: 'stock' },
  { id: 'stock:FMG', ticker: 'FMG.AX', name: "Fortescue Ltd", type: 'stock' },
  { id: 'stock:GMG', ticker: 'GMG.AX', name: "Goodman Group", type: 'stock' },
  { id: 'stock:WDS', ticker: 'WDS.AX', name: "Woodside Energy Group Ltd", type: 'stock' },
  { id: 'stock:TLS', ticker: 'TLS.AX', name: "Telstra Group", type: 'stock' },
  { id: 'stock:WOW', ticker: 'WOW.AX', name: "Woolworths Group", type: 'stock' },
  { id: 'stock:RIO', ticker: 'RIO.AX', name: "Rio Tinto", type: 'stock' },
  { id: 'stock:TCL', ticker: 'TCL.AX', name: "Transurban Group", type: 'stock' },
  { id: 'stock:ALL', ticker: 'ALL.AX', name: "Aristocrat Leisure Limited", type: 'stock' },
  { id: 'stock:REA', ticker: 'REA.AX', name: "REA Group Limited", type: 'stock' },
  { id: 'stock:COL', ticker: 'COL.AX', name: "Coles Group Limited", type: 'stock' },
  { id: 'stock:STO', ticker: 'STO.AX', name: "Santos", type: 'stock' },
  { id: 'stock:QBE', ticker: 'QBE.AX', name: "QBE Insurance Group", type: 'stock' },
  { id: 'stock:FPH', ticker: 'FPH.AX', name: "Fisher & Paykel Healthcare", type: 'stock' },
  { id: 'stock:XRO', ticker: 'XRO.AX', name: "Xero Limited", type: 'stock' },
  { id: 'stock:WTC', ticker: 'WTC.AX', name: "WiseTech Global Limited", type: 'stock' },
  { id: 'stock:PME', ticker: 'PME.AX', name: "Pro Medicus Limited", type: 'stock' },
  { id: 'stock:COH', ticker: 'COH.AX', name: "Cochlear Limited", type: 'stock' },
  { id: 'stock:SUN', ticker: 'SUN.AX', name: "Suncorp Group Limited", type: 'stock' },
  { id: 'stock:AMC', ticker: 'AMC.AX', name: "Amcor plc", type: 'stock' },
  { id: 'stock:BXB', ticker: 'BXB.AX', name: "Brambles Limited", type: 'stock' },
  { id: 'stock:RMD', ticker: 'RMD.AX', name: "ResMed Inc.", type: 'stock' },
  { id: 'stock:CPU', ticker: 'CPU.AX', name: "Computershare Limited", type: 'stock' },
  { id: 'stock:JHX', ticker: 'JHX.AX', name: "James Hardie Industries plc", type: 'stock' },
  { id: 'stock:SHL', ticker: 'SHL.AX', name: "Sonic Healthcare Limited", type: 'stock' },
  { id: 'stock:ORG', ticker: 'ORG.AX', name: "Origin Energy Limited", type: 'stock' },
  { id: 'stock:ASX', ticker: 'ASX.AX', name: "ASX Limited", type: 'stock' },
  { id: 'stock:MPL', ticker: 'MPL.AX', name: "Medibank Private Limited", type: 'stock' },
  { id: 'stock:AZJ', ticker: 'AZJ.AX', name: "Aurizon Holdings Limited", type: 'stock' },
  { id: 'stock:IAG', ticker: 'IAG.AX', name: "Insurance Australia Group", type: 'stock' },
  { id: 'stock:S32', ticker: 'S32.AX', name: "South32 Limited", type: 'stock' },
  { id: 'stock:SGP', ticker: 'SGP.AX', name: "Stockland", type: 'stock' },
  { id: 'stock:MGR', ticker: 'MGR.AX', name: "Mirvac Group", type: 'stock' },
  { id: 'stock:GPT', ticker: 'GPT.AX', name: "The GPT Group", type: 'stock' },
  { id: 'stock:DXS', ticker: 'DXS.AX', name: "Dexus", type: 'stock' },
  { id: 'stock:VCX', ticker: 'VCX.AX', name: "Vicinity Centres", type: 'stock' },
  { id: 'stock:CHC', ticker: 'CHC.AX', name: "Charter Hall Group", type: 'stock' },
  { id: 'stock:APA', ticker: 'APA.AX', name: "APA Group", type: 'stock' },
  { id: 'stock:AGL', ticker: 'AGL.AX', name: "AGL Energy Limited", type: 'stock' },
  { id: 'stock:ORI', ticker: 'ORI.AX', name: "Orica Limited", type: 'stock' },
  { id: 'stock:BSL', ticker: 'BSL.AX', name: "BlueScope Steel Limited", type: 'stock' },
  { id: 'stock:TWE', ticker: 'TWE.AX', name: "Treasury Wine Estates Limited", type: 'stock' },
  { id: 'stock:TPG', ticker: 'TPG.AX', name: "TPG Telecom Limited", type: 'stock' },
  { id: 'stock:CAR', ticker: 'CAR.AX', name: "CAR Group Limited", type: 'stock' },
  { id: 'stock:SEK', ticker: 'SEK.AX', name: "SEEK Limited", type: 'stock' },
  { id: 'stock:DMP', ticker: 'DMP.AX', name: "Domino's Pizza Enterprises Limited", type: 'stock' },
  { id: 'stock:JBH', ticker: 'JBH.AX', name: "JB Hi-Fi Limited", type: 'stock' },
  { id: 'stock:HVN', ticker: 'HVN.AX', name: "Harvey Norman Holdings Limited", type: 'stock' },
  { id: 'stock:SUL', ticker: 'SUL.AX', name: "Super Retail Group Limited", type: 'stock' },
  { id: 'stock:PMV', ticker: 'PMV.AX', name: "Premier Investments Limited", type: 'stock' },
  { id: 'stock:LOV', ticker: 'LOV.AX', name: "Lovisa Holdings Limited", type: 'stock' },
  { id: 'stock:BRG', ticker: 'BRG.AX', name: "Breville Group Limited", type: 'stock' },
  { id: 'stock:REH', ticker: 'REH.AX', name: "Reece Limited", type: 'stock' },
  { id: 'stock:MIN', ticker: 'MIN.AX', name: "Mineral Resources Limited", type: 'stock' },
  { id: 'stock:PLS', ticker: 'PLS.AX', name: "PLS Group Limited", type: 'stock' },
  { id: 'stock:IGO', ticker: 'IGO.AX', name: "IGO Limited", type: 'stock' },
  { id: 'stock:NST', ticker: 'NST.AX', name: "Northern Star Resources Limited", type: 'stock' },
  { id: 'stock:EVN', ticker: 'EVN.AX', name: "Evolution Mining Limited", type: 'stock' },
  { id: 'stock:WHC', ticker: 'WHC.AX', name: "Whitehaven Coal Limited", type: 'stock' },
  { id: 'stock:NHC', ticker: 'NHC.AX', name: "New Hope Corporation Limited", type: 'stock' },
  { id: 'stock:YAL', ticker: 'YAL.AX', name: "Yancoal Australia Ltd", type: 'stock' },
  { id: 'stock:BPT', ticker: 'BPT.AX', name: "Beach Energy Limited", type: 'stock' },
  { id: 'stock:KAR', ticker: 'KAR.AX', name: "Karoon Energy Ltd", type: 'stock' },
  { id: 'stock:MND', ticker: 'MND.AX', name: "Monadelphous Group Limited", type: 'stock' },
  { id: 'stock:WOR', ticker: 'WOR.AX', name: "Worley Limited", type: 'stock' },
  { id: 'stock:ALX', ticker: 'ALX.AX', name: "Atlas Arteria Limited", type: 'stock' },
  { id: 'stock:ALD', ticker: 'ALD.AX', name: "Ampol Limited", type: 'stock' },
  { id: 'stock:VEA', ticker: 'VEA.AX', name: "Viva Energy Group Limited", type: 'stock' },
  { id: 'stock:AMP', ticker: 'AMP.AX', name: "AMP Limited", type: 'stock' },
  { id: 'stock:MFG', ticker: 'MFG.AX', name: "Magellan Financial Group Limited", type: 'stock' },
  { id: 'stock:PPT', ticker: 'PPT.AX', name: "Perpetual Limited", type: 'stock' },
  { id: 'stock:NWL', ticker: 'NWL.AX', name: "Netwealth Group Limited", type: 'stock' },
  { id: 'stock:HUB', ticker: 'HUB.AX', name: "HUB24 Limited", type: 'stock' },
  { id: 'stock:QAN', ticker: 'QAN.AX', name: "Qantas Airways Limited", type: 'stock' },
  { id: 'stock:FLT', ticker: 'FLT.AX', name: "Flight Centre Travel Group Limited", type: 'stock' },
  { id: 'stock:WEB', ticker: 'WEB.AX', name: "Web Travel Group Limited", type: 'stock' },
  { id: 'stock:CTD', ticker: 'CTD.AX', name: "Corporate Travel Management Limited", type: 'stock' },
  { id: 'stock:SDF', ticker: 'SDF.AX', name: "Steadfast Group Limited", type: 'stock' },
  { id: 'stock:NHF', ticker: 'NHF.AX', name: "nib holdings limited", type: 'stock' },
  { id: 'stock:ANN', ticker: 'ANN.AX', name: "Ansell Limited", type: 'stock' },
  { id: 'stock:CWY', ticker: 'CWY.AX', name: "Cleanaway Waste Management Limited", type: 'stock' },
  { id: 'stock:EDV', ticker: 'EDV.AX', name: "Endeavour Group Limited", type: 'stock' },
  { id: 'stock:TAH', ticker: 'TAH.AX', name: "Tabcorp Holdings Limited", type: 'stock' },
  { id: 'stock:LNW', ticker: 'LNW.AX', name: "Light & Wonder, Inc.", type: 'stock' },
  { id: 'stock:ALU', ticker: 'ALU.AX', name: "Altium Limited", type: 'stock' },
  { id: 'stock:APE', ticker: 'APE.AX', name: "Eagers Automotive Limited", type: 'stock' },
  { id: 'stock:BOQ', ticker: 'BOQ.AX', name: "Bank of Queensland Limited", type: 'stock' },
  { id: 'stock:BEN', ticker: 'BEN.AX', name: "Bendigo and Adelaide Bank Limited", type: 'stock' },
  { id: 'stock:SGR', ticker: 'SGR.AX', name: "The Star Entertainment Group Limited", type: 'stock' },
  { id: 'stock:LTR', ticker: 'LTR.AX', name: "Liontown Resources Limited", type: 'stock' },
  { id: 'stock:CDA', ticker: 'CDA.AX', name: "Codan Limited", type: 'stock' },
  { id: 'stock:NXT', ticker: 'NXT.AX', name: "NEXTDC Limited", type: 'stock' },
  { id: 'stock:MP1', ticker: 'MP1.AX', name: "Megaport Limited", type: 'stock' },
  { id: 'stock:ARB', ticker: 'ARB.AX', name: "ARB Corporation Limited", type: 'stock' },
  { id: 'stock:BAP', ticker: 'BAP.AX', name: "Bapcor Limited", type: 'stock' },
  { id: 'stock:RWC', ticker: 'RWC.AX', name: "Reliance Worldwide Corporation Limited", type: 'stock' },
  { id: 'stock:A2M', ticker: 'A2M.AX', name: "The a2 Milk Company Limited", type: 'stock' },
  { id: 'stock:CGF', ticker: 'CGF.AX', name: "Challenger Limited", type: 'stock' },
  { id: 'stock:DOW', ticker: 'DOW.AX', name: "Downer EDI Limited", type: 'stock' },
  { id: 'stock:SOL', ticker: 'SOL.AX', name: "Washington H. Soul Pattinson", type: 'stock' },
  { id: 'stock:RRL', ticker: 'RRL.AX', name: "Regis Resources Limited", type: 'stock' },
  { id: 'stock:PRU', ticker: 'PRU.AX', name: "Perseus Mining Limited", type: 'stock' },
  { id: 'stock:CMM', ticker: 'CMM.AX', name: "Capricorn Metals Ltd", type: 'stock' },
  { id: 'stock:RSG', ticker: 'RSG.AX', name: "Resolute Mining Limited", type: 'stock' },

  // ── ASX ETFs ────────────────────────────────────────────────────────────────
  { id: 'etf:VAS', ticker: 'VAS.AX', name: "ASX 300", type: 'etf' },
  { id: 'etf:VGS', ticker: 'VGS.AX', name: "Vanguard MSCI Index International Shares ETF", type: 'etf' },
  { id: 'etf:VDHG', ticker: 'VDHG.AX', name: "Vanguard Diversified High Growth Index ETF", type: 'etf' },
  { id: 'etf:A200', ticker: 'A200.AX', name: "BetaShares Australia 200 ETF", type: 'etf' },
  { id: 'etf:IVV', ticker: 'IVV.AX', name: "iShares S&P 500 ETF", type: 'etf' },
  { id: 'etf:NDQ', ticker: 'NDQ.AX', name: "BetaShares NASDAQ 100 ETF", type: 'etf' },
  { id: 'etf:IOZ', ticker: 'IOZ.AX', name: "iShares Core S&P/ASX 200 ETF", type: 'etf' },
  { id: 'etf:IOO', ticker: 'IOO.AX', name: "iShares Global 100 ETF (AU)", type: 'etf' },
  { id: 'etf:VTS', ticker: 'VTS.AX', name: "Vanguard US Total Market Shares Index ETF", type: 'etf' },
  { id: 'etf:VEU', ticker: 'VEU.AX', name: "Vanguard All-World ex-US Shares Index ETF", type: 'etf' },
  { id: 'etf:VAP', ticker: 'VAP.AX', name: "Vanguard Australian Property Securities Index ETF", type: 'etf' },
  { id: 'etf:ETHI', ticker: 'ETHI.AX', name: "BetaShares Global Sustainability Leaders ETF", type: 'etf' },
  { id: 'etf:HACK', ticker: 'HACK.AX', name: "BetaShares Global Cybersecurity ETF", type: 'etf' },
  { id: 'etf:ACDC', ticker: 'ACDC.AX', name: "Global X Battery Tech & Lithium ETF", type: 'etf' },
  { id: 'etf:QUAL', ticker: 'QUAL.AX', name: "VanEck MSCI International Quality ETF", type: 'etf' },
  { id: 'etf:GEAR', ticker: 'GEAR.AX', name: "BetaShares Geared Australian Equities ETF", type: 'etf' },
  { id: 'etf:STW', ticker: 'STW.AX', name: "ASX 200", type: 'etf' },
];

const STOCK_TICKER_BY_ID: Record<string, string> = Object.fromEntries(
  SUPPORTED_STOCKS.map((s) => [s.id, s.ticker])
);

/** Yahoo Finance fetch ticker for an asset id. Curated entries may map to a
 *  suffixed ticker (e.g. `etf:VAS` → `VAS.AX` for the ASX listing); anything
 *  else falls back to the raw id ticker. Use this — not tickerFromId — wherever
 *  a price is fetched, so exchange suffixes never pollute the display. */
export function yahooTicker(id: string): string {
  return STOCK_TICKER_BY_ID[id] ?? tickerFromId(id);
}

/** Curated stocks/ETFs as CoinListItems for the selector (locally filtered,
 *  like metals). The display `symbol` stays clean (no ".AX" suffix). */
export const SUPPORTED_STOCK_ITEMS: CoinListItem[] = SUPPORTED_STOCKS.map((s) => ({
  id:            s.id,
  symbol:        tickerFromId(s.id),
  name:          s.name,
  image:         '',
  marketCapRank: 0,
  currentPrice:  0,
  marketCap:     0,
  volume24h:     0,
  high24h:       0,
  low24h:        0,
}));
